import axios from "axios";
import professionOptions from '../profession.json';

const getApiHeaders = (token) => {
  return { Authorization: token ? `Bearer ${token}` : null };
};

/**
 * Rich text as a single readable line, for table cells and tooltips.
 *
 * "About the book" is written in Quill, so what is stored is HTML. Rendered
 * as text it reads "<p><em>The Boys: Definitive..." — and the tooltip showed
 * the whole document, trailing runs of "<br></p><p><br></p>" and all.
 *
 * Block ends become a space BEFORE the tags go, or "...power.</p><p>Volume 2"
 * closes up into "power.Volume 2". Entities are decoded AFTER, so a decoded
 * &lt; cannot be mistaken for a tag by the strip that already ran.
 */
export const htmlToPlainText = (value = "") =>
  String(value ?? "")
    .replace(/<\/(p|div|h[1-6]|li|tr|blockquote)>/gi, " ")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#0?39;|&apos;/gi, "'")
    // Last, so an entity it produces is not decoded again: "&amp;lt;" is the
    // text "&lt;", not a "<".
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim();

// Tag options for the post form. Tags aren't language-scoped (a single
// keyword), so unlike genres there's no per-language filtering.
// A taxonomy list that hasn't answered in this long is treated as failed, so
// the form can say so and offer Retry instead of showing an empty dropdown.
export const TAXONOMY_TIMEOUT_MS = 15000;

/**
 * Tag options. `{ strict: true }` THROWS on a failure (timeout, 401, a
 * `status: false` answer) instead of returning [] — an empty list and a
 * failed load look the same in a dropdown, and only the second needs Retry.
 */
export const fetchAllTagOptions = async (token, { strict = false } = {}) => {
  try {
    const headers = getApiHeaders(token);
    const { data: response } = await axios.get(`${process.env.REACT_APP_API_ROOT}/api/admin/tag/getall`, { headers, timeout: TAXONOMY_TIMEOUT_MS });
    if (!response.status) throw new Error(response.message || "Tags did not load.");
    return (response?.data || [])
      .map((tag) => ({ label: tag.name, value: tag.tagId }))
      .filter((option) => option.value);
  } catch (error) {
    console.error("Error fetching tags:", error);
    if (strict) throw error;
    return [];
  }
};

/**
 * Sub-pillar options for the post form, as react-select **grouped** options:
 * the pillar is the group heading and its sub-pillars are the choices.
 *
 * Only a sub-pillar is ever selected — its pillar is resolved server-side — so
 * this returns no pillar-level values. The grouping is what makes one dropdown
 * read as "Pillar → Sub Pillar" without needing two.
 */
export const fetchAllSubPillarOptions = async (token, { strict = false } = {}) => {
  try {
    const headers = getApiHeaders(token);
    const { data: response } = await axios.get(`${process.env.REACT_APP_API_ROOT}/api/admin/pillar/sub-pillar-options`, { headers, timeout: TAXONOMY_TIMEOUT_MS });
    if (!response.status) throw new Error(response.message || "Pillars did not load.");

    return (response?.data || []).map((pillar) => ({
      label: pillar.pillarName,
      options: (pillar.subPillars || []).map((sub) => ({
        label: sub.name,
        value: sub.subPillarId,
        pillarName: pillar.pillarName,
      })),
    }));
  } catch (error) {
    console.error("Error fetching sub pillars:", error);
    if (strict) throw error;
    return [];
  }
};

/**
 * Finds a stored sub-pillar id inside grouped options so an edit form can
 * prefill. Grouped options are nested one level, which react-select's `value`
 * can't match against by id on its own.
 */
export const findSubPillarOption = (groupedOptions, subPillarId) => {
  if (!subPillarId) return null;
  const id = subPillarId.toString();
  for (const group of groupedOptions || []) {
    const hit = (group.options || []).find((o) => o.value?.toString() === id);
    if (hit) return hit;
  }
  return null;
};

export const fetchAllGenresByLangOptions = async (language, token) => {
  try {
    const headers = getApiHeaders(token);
    const { data: response } = await axios.get(`${process.env.REACT_APP_API_ROOT}/api/admin/genre/getall`, { headers });

    const allGenres = response?.data?.allGenres || [];

    return allGenres
    .map((genre) => {
      // Fall back to English, then to whatever exists — the same order the
      // backend uses for every other translated read. Without it an unexpected
      // or undefined `language` yields a list of blank rows, which looks
      // identical to having no genres at all.
      const translations = genre.translations ?? [];
      const translation =
        translations.find((lang) => lang.langCode === language) ??
        translations.find((lang) => lang.langCode === 'en') ??
        translations[0];

      return {
        genreName: translation?.name || '',
        genreId: genre._id,
        type: genre.type,
      };
    })
    // A row with no name cannot be identified or picked; better absent.
    .filter((genre) => genre.genreName);
  } catch (error) {
    console.error("Error fetching genres:", error);
    return [];
  }
};

/**
 * `onlySummarized` narrows the list to books with a published summary. A
 * collection's tiles open the summary reader, so offering a book without one
 * builds a tile that leads nowhere. Off by default — the summary and
 * recommender forms want the whole library.
 */
export const fetchAllBookCategoriesByLangOptions = async (language, token, onlySummarized = false) => {
  try {
    const headers = getApiHeaders(token);
    const query = onlySummarized ? "?onlySummarized=1" : "";
    const { data: response } = await axios.get(`${process.env.REACT_APP_API_ROOT}/api/admin/books/getall/booknames-by-language/${language}${query}`, { headers });

    if (response.status) {
      const allBookLangOptions = response?.data || [];

      return allBookLangOptions
      .sort((a, b) => {
        const nameA = a.bookName || '';
        const nameB = b.bookName || '';
        return nameA.localeCompare(nameB);
      })
      .filter((lang) => lang.langCode === language)
      .map((lang) => {
        const normalizedCoverImage = normalizeCoverImage(lang?.coverImage);
        return {
          bookName: lang?.bookName,
          bookId: lang?.bookId,
          authorName: lang?.authorName,
          image: normalizedCoverImage.uploadedImageUrl || normalizedCoverImage.uploadedImageKey || null,
        };
      });
    }
  } catch (error) {
    console.error("Error fetching books:", error);
    return [];
  }
};

export const fetchAllRecommendersByLangOptions = async (language, token) => {
  try {
    const headers = getApiHeaders(token);
    const { data: response } = await axios.get(`${process.env.REACT_APP_API_ROOT}/api/admin/recommend/names`, { headers });

    if (response.status) {
      const allRecommenderByLangOptions = response?.data || [];

      return allRecommenderByLangOptions
        .filter((lang) => lang.langCode === language)
        .map((recommend) => ({
          recommenderName: recommend.recommenderName,
          recommenderId: recommend.recommenderId,
          image: recommend.image,
        }));
    }
  } catch (error) {
    console.error("Error fetching books:", error);
    return [];
  }
};

/**
 * Series whose title matches `input`, as react-select options flagged
 * `isSeries`. The admin Series list endpoint, so a series found here is one
 * that exists on the Series page. Shared by the Picks editor and the
 * Recommender form, which both hold books and series on one shelf.
 * Always resolves to an array.
 */
export const searchSeriesOptions = async (input, token, language = "en") => {
  try {
    const headers = getApiHeaders(token);
    const { data: response } = await axios.post(
      `${process.env.REACT_APP_API_ROOT}/api/admin/series/getall`,
      { filters: { currentPage: 1, itemsPerPage: 20, langCode: language, seriesName: (input || "").trim() } },
      { headers }
    );
    // `data.seriesDocs` — the ADMIN envelope. The reader's controller for the
    // same collection calls it `seriesData`; reading that one came back empty.
    const rows = response?.data?.seriesDocs || [];
    return (Array.isArray(rows) ? rows : []).map((row) => {
      // `translations` is already unwound to the requested language by the
      // aggregation, so it is an object here, not an array.
      const t = row.translations || {};
      const bookCount = (t.books || []).length;
      return {
        value: row._id,
        seriesName: t.title,
        bookCount,
        label: bookCount ? `${t.title} — ${bookCount} books` : t.title,
        // A series usually has no cover of its own; the list hands back its
        // first book's, which is what the Series page shows too.
        image: row.coverImage?.uploadedImageUrl || row.firstBookCover?.uploadedImageUrl,
        isSeries: true,
      };
    });
  } catch (error) {
    console.error("searchSeriesOptions error::", error);
    return [];
  }
};

/**
 * Published summaries as picker options, for the Featured Summary section.
 * Always resolves to an array — callers map over it directly.
 */
export const fetchAllSummaryOptions = async (language, token) => {
  try {
    const headers = getApiHeaders(token);
    // The endpoint returns exactly one row per summary, already resolved to the
    // requested language with a per-summary fallback, so no client-side
    // filtering is needed. (Filtering here previously dropped every summary not
    // translated into the editing language — showing 3 books out of 430.)
    const { data: response } = await axios.get(
      `${process.env.REACT_APP_API_ROOT}/api/admin/summary/names`,
      { headers, params: { langCode: language } }
    );

    if (!response.status) return [];
    return (response?.data || []).filter((row) => row.summaryId);
  } catch (error) {
    console.error("Error fetching summaries:", error);
    return [];
  }
};

/**
 * The Profession & Field options, from the catalogue the admin can edit.
 *
 * This used to read a bundled `profession.json`, so the list could only change
 * by shipping a build. It comes from `/professions/getall` now.
 *
 * The option's value stays `professionId` — the catalogue entry's own field,
 * NOT its `_id`. That is what `findProfessions` and the CSV importer match an
 * author's stored profession against, so an option carrying `_id` would save
 * authors whose profession then shows as nothing in the app.
 *
 * Falls back to the bundled file if the request fails, so a form that is
 * mid-edit still has something to offer rather than an empty picker.
 */
export const fetchAllProfessionOptions = async (token, language) => {
  const toOptions = (list) =>
    list
      .map((field) => ({
        label: field.professionName?.[language] || field.professionName?.en || "",
        value: field.professionId,
      }))
      .filter((option) => option.label)
      .sort((a, b) => a.label.localeCompare(b.label));

  try {
    const { data } = await axios.get(
      `${process.env.REACT_APP_API_ROOT}/api/admin/professions/getall`,
      { headers: getApiHeaders(token) }
    );

    if (data?.status) {
      return toOptions(data.data?.professions || []);
    }

    throw new Error(data?.message || "Professions request failed");
  } catch (error) {
    console.error("Error fetching professions:", error);
    return toOptions(professionOptions);
  }
};

/**
 * The admin names behind Created by / Edited by, for those filter pickers.
 *
 * Names only, from a route any signed-in admin may call — the team list is
 * owner-only because it carries roles and sessions, and a manager filtering by
 * who made a row needs neither.
 */
export const fetchAllActorNames = async (token) => {
  try {
    const { data } = await axios.get(
      `${process.env.REACT_APP_API_ROOT}/api/admin/access/actor-names`,
      { headers: getApiHeaders(token) }
    );
    return Array.isArray(data?.data) ? data.data : [];
  } catch (error) {
    console.error("Error fetching actor names:", error);
    return [];
  }
};

/**
 * Markdown emphasis markers removed from pasted text.
 *
 * The clipboard carries two flavours of the same selection: `text/html` with
 * real `<em>`/`<strong>`, and `text/plain` where many sources (ChatGPT, Notion,
 * anything markdown-backed) write the emphasis as asterisks. A rich-text editor
 * takes the HTML and shows italics; a plain `<textarea>` takes the plain text
 * and shows `**The Internet of Money**`.
 *
 * These fields store plain text, so the markers are noise that would be saved
 * and then rendered literally in the app. The words are kept, the markers go.
 *
 * Deliberately conservative — a marker must hug non-space content to count, so
 * a lone `*` or a `2 * 3` survives.
 */
export const stripPastedMarkdown = (text) =>
  String(text ?? "")
    .replace(/\*\*\*(\S(?:.*?\S)?)\*\*\*/g, "$1")
    .replace(/\*\*(\S(?:.*?\S)?)\*\*/g, "$1")
    .replace(/(^|[^*\w])\*(\S(?:.*?\S)?)\*(?=[^*\w]|$)/g, "$1$2")
    .replace(/(^|[^_\w])__(\S(?:.*?\S)?)__(?=[^_\w]|$)/g, "$1$2")
    .replace(/(^|[^_\w])_(\S(?:.*?\S)?)_(?=[^_\w]|$)/g, "$1$2");

/**
 * Pastes into a plain-text field with the markdown markers taken out.
 *
 * Only intercepts when there is something to strip, so an ordinary paste keeps
 * the browser's own undo history. Inserting by hand means the two things the
 * browser would have done for us have to be done here: honour `maxLength`, and
 * leave the caret after what was pasted instead of at the end of the field.
 */
export const pastePlainText = (event, apply) => {
  const clipboard = event.clipboardData?.getData("text/plain");
  if (!clipboard) return;

  const cleaned = stripPastedMarkdown(clipboard);
  if (cleaned === clipboard) return;

  event.preventDefault();
  const field = event.target;
  const { selectionStart: from, selectionEnd: to, value } = field;
  const limit = Number(field.maxLength);
  const room = limit > 0 ? limit - (value.length - (to - from)) : cleaned.length;
  const inserted = cleaned.slice(0, Math.max(room, 0));

  apply(value.slice(0, from) + inserted + value.slice(to));

  const caret = from + inserted.length;
  window.requestAnimationFrame(() => field.setSelectionRange(caret, caret));
};

/**
 * Downloads a list as CSV. Returns whether a file actually arrived.
 *
 * It does not throw — a failed export must not take the page down with it —
 * but it no longer fails SILENTLY either: every caller used to report success
 * whatever happened, so an unreachable endpoint or an unsupported list name
 * looked exactly like a download the browser had saved.
 */
export const generateCsvFile = async (token, title, filters) => {
  try {
    const headers = getApiHeaders(token);
    const csvResponse = await axios.post(getAPiUrl(title), filters, { headers, responseType: "blob" });

    if (csvResponse.status === 200) {
      const disposition = csvResponse.headers["content-disposition"];
      let filename = `${title}.csv`;

      if (disposition && disposition.includes("filename=")) {
        const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i);
        filename = decodeURIComponent(match?.[1] || match?.[2] || filename);
      }

      const url = window.URL.createObjectURL(new Blob([csvResponse.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      return true;
    }

    console.error("Unexpected response status", csvResponse.status);
    return false;
  } catch (error) {
    console.error("Error generating CSV:", error);
    return false;
  }
};

const getAPiUrl = (title) => {
  const normalizedTitle = title?.trim().toLowerCase();
  
  if (!normalizedTitle) {
    throw new Error('Title parameter is required');
  }

  const apiEndpoints = {
    'genres': 'download/genres-csv',
    'recommenders': 'download/recommenders-csv',
    'books': 'download/books-csv',
    'favorite-authors': 'download/authors-csv',
    'collections': 'download/collections-csv',
    // What the panel calls Journeys; same export.
    'journeys': 'download/collections-csv',
    'summaries': 'download/summaries-csv',
    'series': 'download/series-csv',
    'professions': 'download/professions-csv'
  };

  const endpoint = apiEndpoints[normalizedTitle];
  
  if (!endpoint) {
    const supportedTypes = Object.keys(apiEndpoints).join(', ');
    throw new Error(`Unsupported data type: "${title}". Supported types: ${supportedTypes}`);
  }

  return `${process.env.REACT_APP_API_ROOT}/api/admin/${endpoint}`;
};

export const highlightSearchText = (text, searchValue) => {
  if (!searchValue || !text) {
    return text;
  }
  
  const escapedSearchValue = searchValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const searchRegex = new RegExp(`(${escapedSearchValue})`, 'gi');
  const textParts = text.split(searchRegex);
  
  return textParts.map((part, index) => {
    const isMatch = searchRegex.test(part);
    
    if (isMatch) {
      return (
        <mark 
          key={index} 
          style={{ 
            backgroundColor: '#ffeb3b', 
            padding: '0 2px' 
          }}
        >
          {part}
        </mark>
      );
    }
    
    return part;
  });
};

/**
 * Converts S3 URLs to CloudFront URLs for consistent image delivery.
 * - If URL is already CloudFront, returns as-is
 * - If URL is S3, converts to CloudFront
 * - If null/undefined, returns null
 * - Otherwise returns as-is
 * 
 * @param {string|null|undefined} url - The image URL to convert
 * @returns {string|null} CloudFront URL or null
 * 
 * @example
 * convertToCloudFrontUrl("https://penspace-media.s3.ap-south-1.amazonaws.com/category/abc.jpg")
 * // Returns: "https://dbdbaz61pzgv.cloudfront.net/category/abc.jpg"
 * 
 * convertToCloudFrontUrl("https://dbdbaz61pzgv.cloudfront.net/category/abc.jpg")
 * // Returns: "https://dbdbaz61pzgv.cloudfront.net/category/abc.jpg" (as-is)
 */
export const convertToCloudFrontUrl = (url) => {
  // Handle null or undefined
  if (url === null || url === undefined || url === '') {
    return null;
  }

  // If not a string, return as-is
  if (typeof url !== 'string') {
    return url;
  }

  // If already CloudFront URL, return as-is
  if (url.includes('dbdbaz61pzgv.cloudfront.net')) {
    return url;
  }

  // If S3 URL, convert to CloudFront (handle any S3 bucket: penspace, penspace-media, etc.)
  if (url.includes('.s3.ap-south-1.amazonaws.com') || url.includes('.s3.amazonaws.com')) {
    // Extract the path/key from S3 URL (everything after the domain)
    const s3Path = url.replace(/^https?:\/\/[^\/]+/, '');
    const cloudFrontUrl = `https://dbdbaz61pzgv.cloudfront.net${s3Path}`;
    return cloudFrontUrl;
  }

  // For any other URL format, return as-is
  return url;
};

/**
 * Normalizes coverImage field from API/DB to a consistent object format.
 * Handles three formats:
 * 1. null → { uploadedImageUrl: null, uploadedImageKey: null }
 * 2. string (legacy) → { uploadedImageUrl: string (with ImageKit prefix if needed), uploadedImageKey: string }
 * 3. object → { uploadedImageUrl: string, uploadedImageKey: string } (returns as-is if valid)
 * 
 * All uploadedImageUrl values are converted to CloudFront URLs for consistent delivery.
 * 
 * @param {null|string|object} coverImage - The coverImage value from API/DB
 * @returns {object} Normalized object with uploadedImageUrl and uploadedImageKey properties
 * 
 * @example
 * normalizeCoverImage(null) // { uploadedImageUrl: null, uploadedImageKey: null }
 * normalizeCoverImage("category/abc.png") // { uploadedImageUrl: "https://...", uploadedImageKey: "category/abc.png" }
 * normalizeCoverImage({ uploadedImageUrl: "https://...", uploadedImageKey: "key" }) // returns as-is
 */
export const normalizeCoverImage = (coverImage) => {
  // Always return consistent format: { uploadedImageKey: string | null, uploadedImageUrl: string | null }
  
  // Handle null or undefined
  if (coverImage === null || coverImage === undefined) {
    return {
      uploadedImageKey: null,
      uploadedImageUrl: null
    };
  }

  // Handle string (legacy format: S3 key like "category/abc.png" or full URL)
  if (typeof coverImage === 'string') {
    // If it's already a full URL, convert to CloudFront if needed
    if (coverImage.startsWith('http://') || coverImage.startsWith('https://')) {
      return {
        uploadedImageKey: coverImage, // Preserve original for reference
        uploadedImageUrl: convertToCloudFrontUrl(coverImage)
      };
    }
    
    // Otherwise, it's likely a path like "category/abc.png" (S3 key)
    // Construct full URL with ImageKit prefix if available, then convert to CloudFront
    const imageKitUrl = process.env.REACT_APP_IMAGEKIT 
      ? `${process.env.REACT_APP_IMAGEKIT}${coverImage}`
      : coverImage;
    
    return {
      uploadedImageKey: coverImage, // Preserve the S3 key
      uploadedImageUrl: convertToCloudFrontUrl(imageKitUrl)
    };
  }

  // Handle object format
  if (typeof coverImage === 'object' && !Array.isArray(coverImage)) {
    // Extract values safely, ensuring we always return the expected format
    const uploadedImageUrl = coverImage.uploadedImageUrl ?? null;
    const uploadedImageKey = coverImage.uploadedImageKey ?? null;
    
    // If object has the expected structure, normalize and convert URL to CloudFront.
    // The backend returns raw S3 URLs and (for covers) also thumb/detail/master variants;
    // every URL must get the S3 -> CloudFront swap, otherwise the variants bypass the CDN.
    if ('uploadedImageUrl' in coverImage || 'uploadedImageKey' in coverImage) {
      return {
        ...coverImage, // preserve extra fields (mediaType, etc.)
        uploadedImageKey: uploadedImageKey,
        uploadedImageUrl: convertToCloudFrontUrl(uploadedImageUrl),
        ...(coverImage.thumb != null && { thumb: convertToCloudFrontUrl(coverImage.thumb) }),
        ...(coverImage.detail != null && { detail: convertToCloudFrontUrl(coverImage.detail) }),
        ...(coverImage.master != null && { master: convertToCloudFrontUrl(coverImage.master) }),
      };
    }
    
    // If it's an empty object or unexpected structure, return null values
    return {
      uploadedImageKey: null,
      uploadedImageUrl: null
    };
  }

  // Fallback for any other unexpected format (arrays, functions, etc.)
  return {
    uploadedImageKey: null,
    uploadedImageUrl: null
  };
};
/**
 * Visible character count for a rich-text value.
 *
 * "About the Book" is HTML — the summary form has always edited it with Quill,
 * and the app renders it through `flutter_html`. Counting `value.length` would
 * therefore count the markup, so a description could read "550/4500" while
 * showing barely two sentences.
 *
 * Strips tags and decodes the handful of entities Quill emits, so the number
 * next to the field is what the reader will actually see.
 */
export const plainTextLength = (value) => {
  if (!value) return 0;
  return String(value)
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .trim().length;
};

/**
 * True when a rich-text value renders as nothing.
 *
 * Quill never yields "" — an emptied editor emits `<p><br></p>`. A plain
 * `!value || !value.trim()` check therefore reads that as content, which is
 * how a book with no description could pass "Please write some words about
 * the book" and store markup with no words in it.
 */
export const isBlankHtml = (value) => plainTextLength(value) === 0;

/**
 * Seconds -> "02:15" / "1:02:05", matching `formattedTime` on the audio object.
 *
 * The backend stores a formatted string too, but the list projection sends only
 * summed seconds — a total across chapters has no single stored string to read.
 */
export const formatAudioDuration = (totalSeconds) => {
  const s = Math.max(0, Math.round(Number(totalSeconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  // Minutes padded, so this matches the backend's `formattedTime` exactly. An
  // unpadded "2:15" here against a stored "02:15" elsewhere would put two
  // duration formats on the same screen for no reason.
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
};
